import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren
} from 'react';
import {
  useGetMcpAiSettingsQuery,
  useListMcpProjectModelsQuery,
  useUpdateMcpAiSettingsMutation
} from '../api/mcpApi';
import type { McpProjectAiModel } from '../types/mcpApiTypes';

export type McpChatModelSelection =
  | { type: 'auto' }
  | { type: 'model'; modelId: string };

type McpChatModelContextValue = {
  models: McpProjectAiModel[];
  selection: McpChatModelSelection | null;
  selectedModel: McpProjectAiModel | null;
  isAuto: boolean;
  isLoading: boolean;
  setSelection: (next: McpChatModelSelection) => void;
};

const McpChatModelContext = createContext<McpChatModelContextValue | null>(
  null
);

export const McpChatModelProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data: models = [], isLoading: modelsLoading } =
    useListMcpProjectModelsQuery();
  const { data: settings, isLoading: settingsLoading } =
    useGetMcpAiSettingsQuery();
  const [updateSettings] = useUpdateMcpAiSettingsMutation();
  const [selection, setSelectionState] =
    useState<McpChatModelSelection | null>(null);

  // The server is the source of truth for both regular models and Auto.
  useEffect(() => {
    if (!settings) return;
    const settingsModel =
      (settings.projectAiModelId
        ? models.find(model => model.id === settings.projectAiModelId)
        : undefined) ??
      models.find(
        model =>
          model.provider === settings.provider && model.tier === settings.tier
      );
    if (!settingsModel) return;
    setSelectionState(
      settingsModel.provider === 'auto'
        ? { type: 'auto' }
        : { type: 'model', modelId: settingsModel.id }
    );
  }, [
    models,
    settings?.projectAiModelId,
    settings?.provider,
    settings?.tier
  ]);

  // Only if settings never arrive: fall back to Auto, then first available model.
  useEffect(() => {
    if (
      settingsLoading ||
      settings ||
      selection ||
      models.length === 0
    ) {
      return;
    }
    if (models.some(m => m.provider === 'auto')) {
      setSelectionState({ type: 'auto' });
      return;
    }
    const mid = models.find(m => m.provider === 'google' && m.tier === 'mid');
    const fallback = mid ?? models[0];
    if (fallback) {
      setSelectionState({
        type: 'model',
        modelId: fallback.id
      });
    }
  }, [models, selection, settings, settingsLoading]);

  const setSelection = useCallback(
    (next: McpChatModelSelection) => {
      setSelectionState(next);
      if (next.type === 'auto') {
        const autoModel = models.find(model => model.provider === 'auto');
        if (autoModel) {
          void updateSettings({ projectAiModelId: autoModel.id });
        }
        return;
      }
      const selected =
        next.type === 'model'
          ? models.find(model => model.id === next.modelId)
          : undefined;
      if (!selected) return;
      void updateSettings({
        projectAiModelId: selected.id
      });
    },
    [models, updateSettings]
  );

  const selectedModel = useMemo(() => {
    if (!selection || selection.type !== 'model') return null;
    return models.find(model => model.id === selection.modelId) ?? null;
  }, [models, selection]);

  const isAuto = selection?.type === 'auto';

  const value = useMemo<McpChatModelContextValue>(
    () => ({
      models,
      selection,
      selectedModel,
      isAuto,
      isLoading: modelsLoading || settingsLoading,
      setSelection
    }),
    [
      models,
      selection,
      selectedModel,
      isAuto,
      modelsLoading,
      settingsLoading,
      setSelection
    ]
  );

  return (
    <McpChatModelContext.Provider value={value}>
      {children}
    </McpChatModelContext.Provider>
  );
};

export function useMcpChatModel(): McpChatModelContextValue {
  const ctx = useContext(McpChatModelContext);
  if (!ctx) {
    throw new Error('useMcpChatModel must be used within McpChatModelProvider');
  }
  return ctx;
}

export function useOptionalMcpChatModel(): McpChatModelContextValue | null {
  return useContext(McpChatModelContext);
}
